import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { getMimeTypeDisplayLabel } from '@aline/common';
import { link } from '@aline/routes';
import { useApolloClient } from '@apollo/client/react';
import { Typography } from '@mui/material';
import type { GridColDef, GridFilterItem } from '@mui/x-data-grid-premium';
import {
  DataGridPremium,
  gridClasses,
  GridLogicOperator,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { Helmet } from 'react-helmet';
import type {
  DisplayMode,
  DocumentStatusFilterType,
  ReportRow,
  RepositoryDocumentsView,
  RepositoryView,
} from 'src/components';
import {
  AlineCheckbox,
  allContractTypeFilters,
  allFileTypeFilters,
  allLabelFilters,
  allOwnerFilters,
  allPartyFilters,
  ContractTypeCell,
  dateFilterOperators,
  dateValueGetter,
  DocumentLabelCell,
  DocumentsListFooter,
  DocumentStatusFilter,
  documentStatusFilterOperator,
  getIsDocumentStatusFilterItem,
  getIsReportGridRow,
  getStringOperatorsWithSafeIsAnyOf,
  GridStatusCell,
  ListEmpty,
  ReportColumnsPanel,
  ReportFiltersPanel,
  RepositoryDocumentNameCell,
  RepositoryToolbar,
  signatureDetectedFilterOperator,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { ReportEmptyIcon } from 'src/components/icons/ReportEmptyIcon';
import { getEstimatedReportRowHeight } from 'src/components/reports/Report';
import { RepositoryDropzone } from 'src/components/repository-dropzone/RepositoryDropzone';
import { useSelectCurrentUser } from 'src/gql';
import { formatDateForDisplay, openLink } from 'src/helpers';
import {
  evictDocumentsFromReportRowsCache,
  FiltersAnchorProvider,
  refreshDataGrid,
  useClearRowGroupingInRelationshipMode,
  useDescendantSelectionPropagation,
  useDocumentGrid,
  useDocumentGridCommonSx,
  useDropDeletedQuestionsFromGrouping,
  useFiltersAnchorState,
  useQuestionGroupableColumns,
  useStickySelectAllAcrossFetches,
  useViewState,
} from 'src/hooks';
import type { AffectedRelationshipParents } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { DocumentGridContextProviders } from './RepositoryContextProviders';
import { RepositoryPartiesCell } from './RepositoryDocumentPartyCell';
import { useApplyRepositoryView } from './RepositoryViewsDropdown';
import { useApplyRepositoryUrlFilter } from './use-apply-repository-url-filter';

export const documentListButtonId = 'document-quick-view';

const useStyles = makeStyles()(theme => ({
  listContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    flexGrow: 1,
    minHeight: 0,
    position: 'relative' as const, // Required for FloatingActionButton positioning
  },
  missingMatchesChip: {
    '& .MuiChip-deleteIcon': {
      margin: 'unset',
    },
    '& .MuiChip-label': {
      color: theme.appColors.missingMatchesChipText,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: '16px',
      padding: 0,
    },
    '&:hover': {
      backgroundColor: 'rgba(250, 113, 91, 0.25)',
    },
    backgroundColor: theme.appColors.missingMatchesChipBackground,
    border: '1px solid transparent',
    borderRadius: 16,
    gap: theme.spacing(0.5),
    height: 32,
    padding: theme.spacing(1),
  },
  missingMatchesChipActive: {
    border: `1px solid ${theme.appColors.missingMatchesChipText}`,
  },
  relationshipStatusChip: {
    '& .MuiChip-deleteIcon': {
      margin: 'unset',
    },
    '& .MuiChip-label': {
      color: theme.appColors.primary,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: '16px',
      padding: 0,
    },
    backgroundColor: theme.appColors.relationshipReviewChipBackground,
    border: '1px solid transparent',
    borderRadius: 16,
    gap: theme.spacing(0.5),
    height: 32,
    padding: theme.spacing(1),
  },
  relationshipStatusChipActive: {
    border: `1px solid ${theme.appColors.primary}`,
  },
  xGrid: {
    flexGrow: 1,
    minHeight: 0, // Allow shrinking
    [`& .${gridClasses.row}`]: {
      [`&:hover #${documentListButtonId}`]: {
        visibility: 'visible' as const,
      },
    },
  },
}));

type RepositoryDocumentGridDef = GridColDef<ReportRow>;

interface RepositoryAllDocumentsProps {
  activeRepositoryView: RepositoryDocumentsView;
  documentStatusFilter: DocumentStatusFilterType;
  onRepositoryViewChange: (view: RepositoryView) => void;
  repositoryId: UUID;
  /** Bumped each time documents are restored from Trash, so this grid refetches. */
  restoredDocumentsVersion: number;
  setDocumentStatusFilter: React.Dispatch<React.SetStateAction<DocumentStatusFilterType>>;
}

export const RepositoryAllDocuments = React.memo(function RepositoryAllDocuments({
  activeRepositoryView,
  documentStatusFilter,
  onRepositoryViewChange,
  repositoryId,
  restoredDocumentsVersion,
  setDocumentStatusFilter,
}: RepositoryAllDocumentsProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiRef();
  const handleRowSelectionModelChange = useDescendantSelectionPropagation(apiRef);
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const applyRepositoryView = useApplyRepositoryView(apiRef);
  const appliedRepositoryViewRef = useRef(activeRepositoryView);
  const refreshedRestoreVersionRef = useRef(restoredDocumentsVersion);
  const [isUploadPickerOpen, setIsUploadPickerOpen] = React.useState(false);
  const currentUser = useSelectCurrentUser();
  const isSignatureDetectionEnabled = Boolean(currentUser?.org.signatureDetectionEnabled);

  // --- View state (display mode, filter toggles) ---
  const { dispatch, displayMode, missingMatchesOnly, unconfirmedRelationshipsOnly } =
    useViewState();

  const onNavigate = useCallback((documentId: string) => {
    openLink(link.DocumentDetail({ documentId }), { title: 'Document' });
  }, []);

  /**
   * The org's extraction questions, offered as grouping choices even though
   * this grid shows no column for them: the server groups by any question in
   * the org, filing a document with no extraction job under "(No answer)".
   */
  const { loading: questionGroupableColumnsLoading, questionGroupableColumns } =
    useQuestionGroupableColumns();

  // --- Shared document grid logic ---
  const {
    clearReportDataSourceCaches,
    contextProviderProps,
    dataSource,
    dataSourceCache,
    detectingRelationshipsCount,
    documentCount,
    effectiveMissingMatchesCount,
    failedRelationshipsCount,
    filterModel,
    getRowClassName,
    groupingColDef,
    handleRowClick,
    isGrouped,
    loadedRowCount,
    onDataSourceError,
    removeDocumentsFromGrid,
    removeGroupFromGrid,
    rowGroupingModel,
    setFilterModel,
    setRowGroupingModel,
    setSortModel,
    sortModel,
    unconfirmedRelationshipsCount,
  } = useDocumentGrid({
    apiRef,
    displayMode,
    missingMatchesOnly,
    onNavigate,
    report: { state: {} },
    reportId: repositoryId,
    unconfirmedRelationshipsOnly,
    virtualGroupableColumns: questionGroupableColumns,
  });
  const client = useApolloClient();
  const onDocumentsTrashed = useCallback(
    (
      documentIds: readonly UUID[],
      affectedRelationshipParents?: AffectedRelationshipParents | null
    ) => {
      evictDocumentsFromReportRowsCache({
        cache: client.cache,
        documentIds,
        reportId: repositoryId,
      });
      removeDocumentsFromGrid(documentIds, affectedRelationshipParents);
    },
    [client, removeDocumentsFromGrid, repositoryId]
  );

  const onDocumentsRestored = useCallback(
    async (_documentIds: readonly UUID[]) => {
      // Refresh DataGrid - clears cache and forces fresh fetch
      clearReportDataSourceCaches();
      refreshDataGrid(apiRef);

      // Refetch queries to show restored documents in recent activity
      await client.refetchQueries({
        include: ['GetMyRecentDocuments'],
      });
    },
    [apiRef, clearReportDataSourceCaches, client]
  );

  /**
   * The view can change while this grid is hidden behind Trash, so it is applied
   * here rather than in the toolbar. The refs keep a re-run with the same view
   * or restore version (such as a tab switch) from resetting the grid.
   */
  useEffect(() => {
    if (activeRepositoryView === appliedRepositoryViewRef.current) return;
    appliedRepositoryViewRef.current = activeRepositoryView;
    applyRepositoryView(activeRepositoryView);
  }, [activeRepositoryView, applyRepositoryView]);

  useEffect(() => {
    if (restoredDocumentsVersion === refreshedRestoreVersionRef.current) return;
    refreshedRestoreVersionRef.current = restoredDocumentsVersion;
    void onDocumentsRestored([]);
  }, [onDocumentsRestored, restoredDocumentsVersion]);

  const applyRepositoryUrlFilter = useCallback(
    (filterItem: GridFilterItem) => {
      apiRef.current?.setFilterModel({
        items: [filterItem],
        logicOperator: GridLogicOperator.And,
      });
    },
    [apiRef]
  );
  useApplyRepositoryUrlFilter(applyRepositoryUrlFilter);

  // --- Callbacks ---
  const handleDisplayModeChange = useCallback(
    (newMode: DisplayMode) => {
      dispatch({ mode: newMode, type: 'setDisplayMode' });
    },
    [dispatch]
  );

  useClearRowGroupingInRelationshipMode({
    displayMode,
    rowGroupingModel,
    setRowGroupingModel,
  });
  useDropDeletedQuestionsFromGrouping({ rowGroupingModel, setRowGroupingModel });
  useStickySelectAllAcrossFetches(apiRef);

  const handleFilterToUnconfirmedRelationships = useCallback(() => {
    dispatch({ type: 'toggleUnconfirmedOnly' });
  }, [dispatch]);

  const handleFilterToMissingMatches = useCallback(() => {
    dispatch({ type: 'toggleMissingMatchesOnly' });
  }, [dispatch]);

  const columnDefs: RepositoryDocumentGridDef[] = useMemo(() => {
    return [
      {
        field: 'name',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        flex: 2.4,
        headerName: 'Document Name',
        minWidth: 260,
        renderCell: RepositoryDocumentNameCell,
        type: 'string',
      },
      {
        field: 'status',
        filterOperators: isSignatureDetectionEnabled
          ? [documentStatusFilterOperator, signatureDetectedFilterOperator]
          : [documentStatusFilterOperator],
        flex: 0.8,
        headerName: 'Status',
        minWidth: 120,
        renderCell: GridStatusCell,
      },
      {
        field: 'type',
        filterOperators: allContractTypeFilters,
        flex: 1,
        headerName: 'Type',
        minWidth: 150,
        renderCell: ContractTypeCell,
      },
      {
        field: 'parties',
        filterOperators: allPartyFilters,
        flex: 1,
        headerName: 'Parties',
        minWidth: 150,
        renderCell: RepositoryPartiesCell,
      },
      {
        field: 'labels',
        filterOperators: allLabelFilters,
        flex: 1,
        headerName: 'Labels',
        minWidth: 150,
        renderCell: DocumentLabelCell,
        sortable: false,
      },
      activeRepositoryView === 'recently-accessed'
        ? {
            field: 'lastAccessedByRequesterAt',
            filterOperators: dateFilterOperators,
            flex: 0.9,
            headerName: 'Last Accessed At',
            minWidth: 170,
            renderCell: cellParams => {
              if (!getIsReportGridRow(cellParams.row)) return null;
              return <Typography>{formatDateForDisplay(cellParams.value)}</Typography>;
            },
            type: 'dateTime',
            valueGetter: dateValueGetter,
          }
        : {
            field: 'createdAt',
            filterOperators: dateFilterOperators,
            flex: 0.9,
            headerName: 'Created At',
            minWidth: 150,
            renderCell: cellParams => {
              if (!getIsReportGridRow(cellParams.row)) return null;
              return <Typography>{formatDateForDisplay(cellParams.value)}</Typography>;
            },
            type: 'dateTime',
            valueGetter: dateValueGetter,
          },
      {
        field: 'owner',
        filterOperators: allOwnerFilters,
        filterable: true,
        flex: 0.9,
        headerName: 'Owner',
        minWidth: 150,
        sortable: false,
        type: 'string',
      },
      {
        field: 'requesterFavorited',
        flex: 0.6,
        headerName: 'Favorited',
        minWidth: 100,
        sortable: false,
        type: 'boolean',
      },
      {
        field: 'fileType',
        filterOperators: allFileTypeFilters,
        flex: 0.7,
        headerName: 'File Type',
        minWidth: 120,
        sortable: false,
        type: 'string',
        valueGetter: (_value, row) => {
          if (!getIsReportGridRow(row)) return null;
          const { latestVersion } = row;
          const mimetype =
            latestVersion.__typename === 'StaticFileVersion'
              ? latestVersion.fileLocation.mimetype
              : null;
          return getMimeTypeDisplayLabel(mimetype);
        },
      },
    ];
  }, [activeRepositoryView, isSignatureDetectionEnabled]);

  const getRowId = useCallback((row: ReportRow): string => String(row.id), []);

  const handleFilterModelChange = useCallback(
    (nextFilterModel: typeof filterModel) => {
      setFilterModel(nextFilterModel);

      const uniqueStatuses = new Set(
        nextFilterModel?.items.flatMap(item =>
          getIsDocumentStatusFilterItem(item) ? (item.value ?? []) : []
        )
      );

      if (uniqueStatuses.size === 0) {
        setDocumentStatusFilter(DocumentStatusFilter.All);
      } else if (uniqueStatuses.size === 1) {
        const [singleStatus] = uniqueStatuses;
        setDocumentStatusFilter(singleStatus ?? DocumentStatusFilter.All);
      } else {
        setDocumentStatusFilter(DocumentStatusFilter.Mixed);
      }
    },
    [setDocumentStatusFilter, setFilterModel]
  );

  const commonSx = useDocumentGridCommonSx(displayMode, loadedRowCount);

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <Helmet>
        <title>Documents</title>
      </Helmet>
      <RepositoryDropzone
        className={classes.listContainer}
        disabled={isUploadPickerOpen}
        documentStatusFilter={documentStatusFilter}
        repositoryId={repositoryId}
        onDocumentsRestored={onDocumentsRestored}
        onDocumentsTrashed={onDocumentsTrashed}
      >
        <DocumentGridContextProviders {...contextProviderProps}>
          <DataGridPremium
            checkboxSelection
            checkboxSelectionVisibleOnly
            disableColumnMenu
            disableRowSelectionOnClick
            keepNonExistentRowsSelected
            lazyLoading
            pagination
            showToolbar
            apiRef={apiRef}
            className={classes.xGrid}
            columns={columnDefs}
            dataSource={dataSource}
            dataSourceCache={dataSourceCache}
            filterModel={filterModel}
            getEstimatedRowHeight={getEstimatedReportRowHeight}
            getRowClassName={getRowClassName}
            getRowHeight={getAutoRowHeight}
            getRowId={getRowId}
            groupingColDef={groupingColDef}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  fileType: false,
                  owner: false,
                  requesterFavorited: false,
                },
              },
              pagination: {
                paginationModel: { page: 0, pageSize: 50 },
              },
              sorting: {
                sortModel: [
                  {
                    field: 'createdAt',
                    sort: 'desc',
                  },
                ],
              },
            }}
            paginationModel={{
              page: 0,
              pageSize: 50,
            }}
            /**
             * `parents: false` is deliberate — checking every child must never auto-check the
             * parent. The built-in propagation is inert on this data-source tree, so
             * `useDescendantSelectionPropagation` is what actually selects descendants here.
             */
            rowSelectionPropagation={{ descendants: true, parents: false }}
            slotProps={{
              columnsPanel: {
                reportId: repositoryId,
              },
              filterPanel: {
                filterableColumns: 'all',
              },
              footer: {
                isRelationshipGrouping: displayMode === 'relationship',
                missingMatchesOnly,
                onDocumentsTrashed,
                onGroupTrashed: removeGroupFromGrid,
                reportId: repositoryId,
                showRowsLockAction: false,
                totalCount: documentCount,
                totalDocumentCount: documentCount,
              },
              loadingOverlay: {
                noRowsVariant: 'skeleton',
              },
              panel: {
                placement: 'bottom-start',
                target: filtersAnchorEl ?? undefined,
              },
              row: {
                questions: [],
              },
              toolbar: {
                activeRepositoryView,
                alertsProps: {
                  detectingRelationshipsCount,
                  displayMode,
                  effectiveMissingMatchesCount,
                  failedRelationshipsCount,
                  missingMatchesOnly,
                  onFilterToMissingMatches: handleFilterToMissingMatches,
                  onFilterToUnconfirmedRelationships: handleFilterToUnconfirmedRelationships,
                  unconfirmedRelationshipsCount,
                  unconfirmedRelationshipsOnly,
                },
                displayMode,
                documentCount,
                documentStatusFilter,
                onDisplayModeChange: handleDisplayModeChange,
                onRepositoryViewChange,
                onRowGroupingModelChange: setRowGroupingModel,
                onUploadPickerOpenChange: setIsUploadPickerOpen,
                repositoryId,
                rowGroupingModel,
                virtualGroupableColumns: questionGroupableColumns,
                virtualGroupableColumnsLoading: questionGroupableColumnsLoading,
              },
            }}
            slots={{
              baseCheckbox: AlineCheckbox,
              baseTooltip: GridAlineTooltip,
              columnsPanel: ReportColumnsPanel,
              filterPanel: ReportFiltersPanel,
              footer: DocumentsListFooter,
              noRowsOverlay: RepositoryEmpty,
              toolbar: RepositoryToolbar,
            }}
            sortModel={sortModel}
            sx={commonSx}
            treeData={isGrouped}
            onDataSourceError={onDataSourceError}
            onFilterModelChange={handleFilterModelChange}
            onRowClick={handleRowClick}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            onSortModelChange={setSortModel}
          />
        </DocumentGridContextProviders>
      </RepositoryDropzone>
    </FiltersAnchorProvider>
  );
});

export function RepositoryEmpty(): JSX.Element {
  return (
    <ListEmpty
      icon={<ReportEmptyIcon />}
      subtitle="Adjust filters or search to change results."
      title="No documents"
    />
  );
}

// A module-level function keeps its identity, so the grid does not re-measure every row on render.
function getAutoRowHeight(): 'auto' {
  return 'auto';
}
